<template>
  <div class="p-4 relative">
    <PlanOverlay
      :visible="clientsPerm == 'none'"
      :reason="clientsPerm == 'none' && isSubUser ? 'permission' : 'plan'"
    />
    <!-- En-tête avec la barre de recherche et le bouton d'ajout -->
    <div class="flex justify-between items-center mb-4">
      <h1 class="m-0 text-2xl select-none font-bold">Clients</h1>
      <button class="callToAction !px-4 select-none" @click="selectedClient=null; popupVisible=true" v-if="clientsPerm == 'modification'">
        Ajouter un client
      </button>
    </div>
    
    <!-- Barre de recherche -->
    <div class="relative mb-4">
      <input 
        type="text" 
        v-model="searchQuery" 
        placeholder="Rechercher un client..." 
        class="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:border-blue-300"
      />
      <span class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
        <i class="fas fa-search"></i>
      </span>
      <span 
        v-if="searchQuery.trim().length" 
        class="cursor-pointer absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
        @click="searchQuery = ''"
      >
        <i class="fas fa-times"></i>
      </span>
    </div>

    <!-- Tableau des clients -->
    <div class="overflow-x-auto bg-white">
      <table class="min-w-full border border-gray-200 shadow-md rounded-lg bg-white">
        <thead class="bg-[#1E1017] text-white font-bold text-left">
          <tr>
            <th class="px-4 py-2 border-b">Nom</th>
            <th class="px-4 py-2 border-b">Prénom</th>
            <th class="px-4 py-2 border-b">Mail</th>
            <th class="px-4 py-2 border-b">Téléphone</th>
            <th class="px-4 py-2 border-b">Adresse</th>
            <th class="px-4 py-2 border-b w-5 !text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="client in filteredClients" :key="client.id" class="hover:bg-red-100 text-left even:bg-red-50 odd:bg-white"
              @dblclick="goToClientDetails(client)">
            <td class="px-4 py-2 border-b border-gray-300 text-gray-700 select-none">{{ client.contacts[0].lastName }}</td>
            <td class="px-4 py-2 border-b border-gray-300 text-gray-700 select-none">{{ client.contacts[0].firstName }}</td>
            <td class="px-4 py-2 border-b border-gray-300 text-gray-700 select-none">{{ client.contacts[0].email }}</td>
            <td class="px-4 py-2 border-b border-gray-300 text-gray-700 select-none">{{ client.contacts[0].phone1 }}</td>
            <td class="px-4 py-2 border-b border-gray-300 text-gray-700 select-none">{{ client.adress }} {{ client.city }} {{ client.postalCode }}</td>
            <td class="px-4 py-2 border-b border-gray-300 text-gray-700 select-none w-5 !text-center">
              <button class="callToAction !px-4 cursor-pointer select-none" @click="goToClientDetails(client)">
                Modifier
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Popup pour ajouter/modifier une client -->
    <PopupClient
      :visible="popupVisible"
      :client="selectedClient"
      :clients="clients"
      @close="popupVisible = false"
      @add="handleAddClient"
      @update="handleUpdateClient"
      @delete="handleDeleteClient"
    />
  </div>
</template>

<script>
import { fetchClients, addClient, updateClient, deleteClient } from '@/services/clientService';
import Header from '@/components/Others/Header.vue';
import PopupClient from '@/components/Popups/PopupClient.vue';
import PlanOverlay from '@/components/Others/PlanOverlay.vue';
import { inject } from 'vue';

export default {
  name: "ShowClients",
  components: {
    Header,
    PopupClient,
    PlanOverlay
  },
  data() {
    return {
      clients: [],
      maxClient: 25,
      popupVisible: false,
      selectedClient: null, // null for add, object for edit
      searchQuery: "",
    };
  },
  setup() {
    const perm = inject('userPerm');
    const currentUser = inject('currentUser');
    return { perm, currentUser };
  },
  computed: {
    clientsPerm() {
      return this.perm().clients;
    },
    isSubUser() {
      const user = this.currentUser && this.currentUser();
      return user ? !!user.mainUserId : false;
    },
    // Filtrer par nom + prénom et par les différents selects
    filteredClients() {
      let result = this.clients;
      // Filtre par recherche (full name)
      if (this.searchQuery.trim()) {
        const search = this.searchQuery.toLowerCase();
        result = result.filter(res =>
          (`${res.contacts[0].firstName || ''} ${res.contacts[0].lastName || ''} ${res.contacts[0].email || ''} ${res.contacts[0].phone1 || ''} ${res.adress || ''} ${res.city || ''} ${res.postalCode || ''}`).toLowerCase().includes(search)
        );
      }

      return result;
    },
  },
  methods: {
    async fetchClients() {
      this.clients = await fetchClients();
      this.clients.forEach(r => {
        r.isEditing = false;
      });
    },
    async handleAddClient(newResource) {
      if (!newResource) return;
      const added = await addClient(newResource);
      this.clients.push(added);
    },
    async handleUpdateClient(updatedResource) {
      if (!updatedResource) return;
      const index = this.clients.findIndex(r => r.id === updatedResource.id);
      if (index !== -1) {
        this.clients.splice(index, 1, { ...updatedResource, isEditing: false });
        await updateClient(updatedResource);
      }
    },
    async handleDeleteClient(resourceId) {
      if (!resourceId) return;
      this.clients = this.clients.filter(r => r.id !== resourceId);
      await deleteClient(resourceId);
    },
    goToClientDetails(client) {
      this.$router.push({ name: 'client-details', params: { id: client.id } });
    }
  },
  mounted() {
    this.fetchClients();
  }
};
</script>

<style scoped>
table {
  border-collapse: separate; /* Nécessaire pour utiliser border-radius */
  border-spacing: 0;
}

/* Arrondir le coin supérieur gauche et supérieur droit du header */
thead tr th:first-child {
  border-top-left-radius: 8px;
}
thead tr th:last-child {
  border-top-right-radius: 8px;
}

/* Coins inférieurs de la dernière ligne */
tbody tr:last-child td:first-child {
  border-bottom-left-radius: 8px;
}
tbody tr:last-child td:last-child {
  border-bottom-right-radius: 8px;
}
</style>